import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GridColDef } from "@mui/x-data-grid";
import StyledDataGrid from "../../shared/StyledDataGrid";
import { enumColumn, isoDateTimeColumn } from "../../shared/UIParts/columnsFormatter";
import { preadviceTasksService } from "../../services/PreadviceTasksService";
import { PreadviceTask, PreAdviceTaskType, TaskStatus } from "../../interfaces/DTO/tasks/PreadviceTasks";
import { formatPreAdviceTaskType, formatTaskStatus, chipColorTaskStatus } from "../../utils/featuresFormatter";
import { useContactNameColumn, useLeadNameColumn } from "../../hooks/useContactNameColumn";

export interface PreadviceTasksTableRef {
  refresh: () => Promise<void>;
}

const PreadviceTasksTable = forwardRef<PreadviceTasksTableRef>((_, ref) => {
  const [data, setData] = useState<PreadviceTask[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<TaskStatus>(TaskStatus.PENDING);
  const [processingNow, setProcessingNow] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string>("");

  useEffect(() => {
    fetchData();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchData,
  }));

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await preadviceTasksService.getAllTasks();
      setData(result);
    } catch (error) {
      console.error("Failed to fetch PreadviceTasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedIds.length === 0) {
      return;
    }

    try {
      // שימוש ב-bulkPartialUpdate במקום לולאה על updateTaskStatus
      const updates = selectedIds.map((id) => ({ id, status: bulkStatus }));
      await preadviceTasksService.bulkPartialUpdate(updates);
      await fetchData();
      setSelectedIds([]);
    } catch (error) {
      console.error("Failed to update PreadviceTasks status:", error);
    }
  };

  const handleBulkStatusChange = (event: SelectChangeEvent<TaskStatus>) => {
    setBulkStatus(event.target.value as TaskStatus);
  };

  const handleRunProcessingNow = async () => {
    setProcessingNow(true);
    setProcessingMessage("");
    try {
      const result = await preadviceTasksService.runProcessingNow();
      setProcessingMessage(`הרצה בוצעה (${result.status}) לחברה ${result.company_id}`);
      await fetchData();
    } catch (error) {
      console.error("Failed to run pre-advice processing:", error);
      setProcessingMessage("אירעה שגיאה בהרצת התהליך");
    } finally {
      setProcessingNow(false);
    }
  };
  const contactNameColumn = useContactNameColumn();
  const leadNameColumn = useLeadNameColumn();

  const columns: GridColDef[] = [
    contactNameColumn,
    leadNameColumn,
    { field: "external_id", headerName: "מזהה חיצוני", width: 180 },
    {
      field: "status",
      headerName: "סטטוס",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={formatTaskStatus(params.value)}
          color={chipColorTaskStatus(params.value)}
          size="small"
        />
      ),
    },
    isoDateTimeColumn("updated_at", "נבדק בתאריך"),
    enumColumn<PreAdviceTaskType>("task_type", "סוג משימה", Object.values(PreAdviceTaskType), formatPreAdviceTaskType),
    { field: "fail_reason", headerName: "סיבת כשל", width: 220 },
    isoDateTimeColumn("created_at", "נוצר בתאריך"),
    { field: "user_id", headerName: "מזהה משתמש", width: 140},

  ];

  return (
    <Box dir="rtl" sx={{ width: "100%", overflowX: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">  משימות טרום יעוץ בתהליך</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="bulk-status-label">סטטוס חדש</InputLabel>
            <Select
              labelId="bulk-status-label"
              label="סטטוס חדש"
              value={bulkStatus}
              onChange={handleBulkStatusChange}
            >
              {Object.values(TaskStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {formatTaskStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={handleRunProcessingNow}
            variant="contained"
            color="primary"
            disabled={processingNow}
          >
            {processingNow ? "מריץ..." : "הרץ תהליך עכשיו"}
          </Button>
          <Button
            onClick={handleBulkStatusUpdate}
            variant="contained"
            color="secondary"
            disabled={selectedIds.length === 0}
          >
            עדכון סטטוס למשימות שנבחרו
          </Button>
          <Button
            onClick={fetchData}
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            {loading ? "טוען..." : "רענון"}
          </Button>
        </Box>
      </Box>

      {processingMessage && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {processingMessage}
        </Typography>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <StyledDataGrid
          rows={data}
          columns={columns}
          initialState={{
            sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          onRowSelectionModelChange={(ids) =>
            setSelectedIds(
              ids
                .map((id) => Number(id))
                .filter((value) => !Number.isNaN(value))
            )
          }
          getRowId={(row) => row.id ?? row.external_id}
        />
      )}
    </Box>
  );
});

export default PreadviceTasksTable;
